/**
 * errorToastMessage.js — the ONE place that turns a caught value (an Error, an
 * axios error, a string, a stray number, `undefined`, anything) into a plain
 * sentence safe to hand to `toast.error()`.
 *
 * gordon-0921-DEV-edit-wheel-red-toast-shows-only-the-number-3: the builder's
 * media-upload catch blocks (Video/Image/Document/Audio block upload, the
 * Video block's Embed URL "save to Recent Media" step) passed the raw caught
 * value straight to `toast.error()` — `toast.error(err)`. react-toastify
 * renders whatever it is handed; a caller is never guaranteed that value is a
 * string. This showed up live as a red toast whose entire content was a bare
 * digit — a real failure with zero explanation of what failed or what to do.
 *
 * Every editor/media-block catch must route its caught value through this
 * helper before it reaches `toast.error()` — enforced by the sourceScan guard
 * tests sitting next to each call site.
 */

const hasNonEmptyStringMessage = (value) =>
  value && typeof value.message === "string" && value.message.trim();

/**
 * @param {any} err whatever a catch block caught — a string, an Error, an
 *   axios error, a number, an object, `undefined`.
 * @param {string} fallback the sentence to show when `err` carries no usable
 *   text. MUST say what failed and, where possible, what to do — never a
 *   generic "Something went wrong."
 * @returns {string} always a non-empty string, never `err` itself unless it
 *   was already a real sentence.
 */
export function toErrorToastMessage(err, fallback) {
  const safeFallback =
    typeof fallback === "string" && fallback.trim()
      ? fallback.trim()
      : "Something went wrong. Please try again.";

  if (typeof err === "string" && err.trim()) return err.trim();

  const serverMessage = err?.response?.data?.message;
  if (typeof serverMessage === "string" && serverMessage.trim()) {
    return serverMessage.trim();
  }

  if (hasNonEmptyStringMessage(err)) return err.message.trim();

  return safeFallback;
}

export default toErrorToastMessage;
